import { useState, type FormEvent } from 'react'
import { Send } from 'lucide-react'
import { Button, FORM_INPUT_CLASS, Switch, EMPLOYMENT_TYPE_LABELS, EMPLOYMENT_TYPES, TIER_LABELS, WORKER_ROLE_LABELS, type EmploymentType, type WorkerRole, type WorkerTier } from '../../core/index.js'
import { useWorkerRoster } from '../../roster/index.js'
import { useFacilityDelivery, useSendStaffMessage } from '../hooks.js'
import type { SendStaffMessageInput, StaffMessage, StaffMessageFilter } from '../types.js'
import { STAFF_MESSAGE_MAX_LENGTH, smsSegmentsOf } from './staffMessageText.js'

const ROLES: readonly WorkerRole[] = ['RN', 'LPN', 'CNA']
const TIERS: readonly WorkerTier[] = ['gold', 'silver', 'bronze', 'probationary']

export interface StaffMessageComposerProps {
  facilityId: string
  /** Pre-addressed to one worker (the roster row's Message button); without it the composer offers the roster filter. */
  worker?: { workerId: string; name: string } | null
  onSent?: (message: StaffMessage) => void
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

/** The roster rows a filter would reach: the same rule the API applies, for the count the confirmation shows. */
function matches(row: { role: WorkerRole; tier: WorkerTier; employmentType?: EmploymentType; leaveDate?: string | null }, filter: StaffMessageFilter, today: string): boolean {
  if (row.leaveDate && row.leaveDate < today) {
    return false
  }
  return (!filter.role || row.role === filter.role) && (!filter.tier || row.tier === filter.tier) && (!filter.employmentType || (row.employmentType ?? 'full_time') === filter.employmentType)
}

/**
 * The composer (SMS plan section 3.7): the staff member's own words to one
 * worker or the roster. The rule above the box is the plan's D3: the text is
 * theirs, the system adds nothing. A roster-wide message is confirmed with
 * the count; Urgent sends through quiet hours.
 */
export function StaffMessageComposer({ facilityId, worker = null, onSent }: StaffMessageComposerProps) {
  const roster = useWorkerRoster(facilityId)
  const delivery = useFacilityDelivery(facilityId)
  const send = useSendStaffMessage(facilityId)
  const [text, setText] = useState('')
  const [urgent, setUrgent] = useState(false)
  const [filter, setFilter] = useState<StaffMessageFilter>({})
  const [confirming, setConfirming] = useState(false)
  const [sent, setSent] = useState<StaffMessage | null>(null)

  const today = new Date().toISOString().slice(0, 10)
  const reached = worker ? 1 : (roster.data ?? []).filter((row) => matches(row, filter, today)).length
  // A unit filter is the API's to judge (home unit or floats to); the count above is without it.
  const countIsEstimate = !worker && Boolean(filter.unitId)
  const textsOff = delivery.data ? !delivery.data.sms.offered || !delivery.data.sms.enabled : false
  const segments = smsSegmentsOf(text)
  const canSend = text.trim().length > 0 && text.length <= STAFF_MESSAGE_MAX_LENGTH && reached > 0 && !send.isPending

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault()
    if (!canSend) {
      return
    }
    if (!worker && !confirming) {
      setConfirming(true)
      return
    }
    const input: SendStaffMessageInput = { text: text.trim(), to: worker ? { workerIds: [worker.workerId] } : { filter }, ...(urgent ? { urgent: true } : {}) }
    try {
      const message = await send.mutateAsync(input)
      setSent(message)
      setText('')
      setUrgent(false)
      setConfirming(false)
      onSent?.(message)
    } catch {
      setConfirming(false)
    }
  }

  const setFilterField = <K extends keyof StaffMessageFilter>(key: K, value: StaffMessageFilter[K] | ''): void => {
    setConfirming(false)
    setFilter((current) => {
      const { [key]: _old, ...rest } = current
      return value === '' || value === undefined ? rest : { ...rest, [key]: value }
    })
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
      <p className="text-[11px] text-ink-mid leading-relaxed">
        {worker ? `To ${worker.name}.` : 'To everyone on the roster the filter matches.'} Your words go as written, in the app and by text where the worker allows it, with the facility's name before them. Nothing is added: no score, no tier, no pay, nobody else's name.
        {textsOff && <span className="text-gold"> Texts are off at this facility, so this reaches the app only.</span>}
      </p>
      {!worker && (
        <div className="grid gap-2 sm:grid-cols-4">
          <select aria-label="Role" className={FORM_INPUT_CLASS} value={filter.role ?? ''} onChange={(event) => setFilterField('role', event.target.value as WorkerRole | '')}>
            <option value="">Every role</option>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {role} · {WORKER_ROLE_LABELS[role]}
              </option>
            ))}
          </select>
          <select aria-label="Tier" className={FORM_INPUT_CLASS} value={filter.tier ?? ''} onChange={(event) => setFilterField('tier', event.target.value as WorkerTier | '')}>
            <option value="">Every tier</option>
            {TIERS.map((tier) => (
              <option key={tier} value={tier}>
                {TIER_LABELS[tier]}
              </option>
            ))}
          </select>
          <select aria-label="Employment" className={FORM_INPUT_CLASS} value={filter.employmentType ?? ''} onChange={(event) => setFilterField('employmentType', event.target.value as EmploymentType | '')}>
            <option value="">Every employment type</option>
            {EMPLOYMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {EMPLOYMENT_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          <input aria-label="Unit" className={FORM_INPUT_CLASS} placeholder="Any unit" value={filter.unitId ?? ''} onChange={(event) => setFilterField('unitId', event.target.value.trim() === '' ? '' : event.target.value.trim())} />
        </div>
      )}
      <textarea
        aria-label="Message"
        className={`${FORM_INPUT_CLASS} min-h-24`}
        maxLength={STAFF_MESSAGE_MAX_LENGTH}
        value={text}
        onChange={(event) => {
          setText(event.target.value)
          setConfirming(false)
        }}
        placeholder="Flu shots in the break room until 4."
      />
      <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-ink-mid">
        <span>
          {text.length} / {STAFF_MESSAGE_MAX_LENGTH} · {segments} {segments === 1 ? 'text segment' : 'text segments'}
        </span>
        <label className="flex items-center gap-2">
          <Switch checked={urgent} onChange={setUrgent} label="Urgent" />
          Urgent · goes through quiet hours and the daily cap
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button icon={Send} type="submit" disabled={!canSend}>
          {send.isPending ? 'Sending…' : confirming ? `Yes, text ${reached}${countIsEstimate ? ' or fewer' : ''} ${reached === 1 ? 'worker' : 'workers'}` : worker ? 'Send' : `Message ${reached} ${reached === 1 ? 'worker' : 'workers'}`}
        </Button>
        {confirming && (
          <button type="button" className="text-[11px] font-bold text-ink-mid hover:text-coral" onClick={() => setConfirming(false)}>
            Cancel
          </button>
        )}
        {!worker && reached === 0 && roster.data && <span className="text-[11px] text-ink-mid">Nobody on the roster matches.</span>}
      </div>
      {send.isError && <p className="text-[11px] text-coral">{errorText(send.error, 'The message could not be sent.')}</p>}
      {sent && !send.isPending && <p className="text-[11px] text-green">Sent to {sent.count} {sent.count === 1 ? 'worker' : 'workers'}.</p>}
    </form>
  )
}
